import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, Cpu, FileCheck2, UserCheck, CheckCircle2, TrendingUp } from 'lucide-react';

type Step = {
  icon: typeof Target;
  label: string;
  detail: string;
  t: string;
};

const steps: Step[] = [
  { icon: Target, label: 'Intent captured', detail: 'Outcome defined before any action is taken.', t: '0.4s' },
  { icon: Cpu, label: 'Executing', detail: 'AI performs the work inside controlled limits.', t: '1.8s' },
  { icon: FileCheck2, label: 'Evidence recorded', detail: 'Every change and source logged for proof.', t: '2.3s' },
  { icon: UserCheck, label: 'Reviewed', detail: 'Operator reviews and approves before completion.', t: '2.8s' },
  { icon: CheckCircle2, label: 'Governed', detail: 'Outcome verified against governance policies.', t: '3.2s' },
  { icon: TrendingUp, label: 'Improved', detail: 'System learns from outcome for future runs.', t: '3.6s' },
];

/**
 * A self-playing "accountable AI work run" console — a live, process-transparent
 * panel in the spirit of modern agent UIs. Steps reveal in sequence, the active
 * step "thinks", then the run completes and loops.
 */
export function LiveWorkConsole() {
  const [visible, setVisible] = useState(1); // start with 1, animate in sequentially
  const logRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const advance = () => {
      setVisible((v) => {
        if (v >= steps.length) {
          // hold on completion, then restart the entire cycle
          timer.current = setTimeout(() => {
            setVisible(1);
            timer.current = setTimeout(advance, 1000);
          }, 2000);
          return v;
        }
        const delay = v <= 1 ? 1000 : 700;
        timer.current = setTimeout(advance, delay);
        return v + 1;
      });
    };
    timer.current = setTimeout(advance, 1200);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  // Auto-scroll to latest step
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTo({ top: logRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [visible]);

  const done = visible >= steps.length;
  const activeIdx = done ? -1 : visible - 1;

  return (
    <div className="w-full">
      <div className="overflow-hidden rounded-2xl border border-white/12 bg-slate-950 shadow-[0_30px_80px_-40px_rgba(7,78,220,0.5)] backdrop-blur-xl">
        {/* window chrome */}
        <div className="flex items-center gap-2 border-b border-white/10 bg-slate-900/60 px-5 py-3">
          <span className="h-2.5 w-2.5 rounded-full bg-[#ED0216]/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#FF8C00]/70" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#4AAC00]/70" />
          <span className="ml-3 text-white/50">work-run</span>
          <span className="text-white/30">/</span>
          <span className="text-white/40">ticca-0417</span>
          <span className="ml-auto flex items-center gap-2 text-[#074EDC]">
            <motion.span
              className="h-1.5 w-1.5 rounded-full bg-[#4AAC00]"
              animate={{ opacity: done ? 1 : [0.3, 1, 0.3] }}
              transition={{ duration: 1.2, repeat: done ? 0 : Infinity }}
            />
            {done ? 'verified' : 'running'}
          </span>
        </div>

        {/* log — fixed height so step reveals never shift page layout */}
        <div ref={logRef} className="flex flex-col-reverse px-5 py-5 overflow-y-auto" style={{ minHeight: '344px', height: '344px' }}>
          <AnimatePresence initial={false}>
            {steps.slice(0, visible).reverse().map((s, i) => {
              const Icon = s.icon;
              const isActive = i === activeIdx;
              return (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ type: 'spring', stiffness: 260, damping: 24 }}
                  className="flex items-start gap-3"
                >
                  <span
                    className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border ${
                      isActive ? 'border-[#074EDC]/60 bg-[#074EDC]/10' : 'border-[#074EDC]/25 bg-white/8'
                    }`}
                  >
                    <Icon className="h-4 w-4 text-[#074EDC]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="text-white">{s.label}</span>
                      <span className="flex-shrink-0 text-white/30">{s.t}</span>
                    </div>
                    <div className="flex items-center gap-1 text-white/60">
                      {s.detail}
                      {isActive && (
                        <motion.span
                          aria-hidden
                          animate={{ opacity: [0, 1, 0] }}
                          transition={{ duration: 1, repeat: Infinity }}
                        >
                          …
                        </motion.span>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* progress footer */}
        <div className="flex items-center gap-3 border-t border-white/10 px-5 py-3">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-[#074EDC] to-[#4AAC00]"
              animate={{ width: `${Math.round((Math.min(visible, steps.length) / steps.length) * 100)}%` }}
              transition={{ type: 'spring', stiffness: 120, damping: 20 }}
            />
          </div>
          <span className="flex-shrink-0 text-white/40">
            {Math.min(visible, steps.length)} / {steps.length} steps
          </span>
        </div>
      </div>
    </div>
  );
}
